/**
 * 회사 로고 등록 — 통합설정 › 회사정보의 "로고" 그룹 (2026-08-25 신규).
 *
 * **관리 콘솔(8444)의 `sections/CompanyLogoGroup.tsx` 를 그대로 옮겨 온 것이다.** 콘솔 쪽은
 * 그대로 남는다 — 옮긴 게 아니라 복사다. 원본과 다른 곳은 import 경로뿐이다.
 *
 * 여기서 등록한 로고가 **좌측 레일 브랜드**를 그대로 교체한다 — 지금 보고 있는 그 화면이다
 * (로고가 있으면 아래 워드마크 텍스트는 감추고 72px 정사각 자리를 꽉 채운다 — NavBar).
 *
 * 업로드 경로: 파일 선택 → 브라우저에서 긴 변 512px·1MB 이하로 축소(lib/image-resize) →
 *   PUT /api/v1/institution/logo { contentType, data(base64) } → chat-server(MinIO 저장).
 *
 * 회사정보 폼의 저장/되돌리기(dirty 판정)와는 **독립**이다 — 로고는 즉시 반영이라
 * 폼 상태에 섞지 않는다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Upload, Trash2, Check } from '../../icons';
import {
  getInstitutionLogo, uploadInstitutionLogo, deleteInstitutionLogo,
  getInstitutionSeal, uploadInstitutionSeal, deleteInstitutionSeal,
  type Institution, type InstitutionLogo,
} from '../../api/institution';
import { resizeImageForUpload } from '../../lib/image-resize';
import { BrandSymbol } from '../../components/BrandSymbol';
// 채택 3단계 — 시안 줄(`variant='row'`)의 단추를 정본 Button 으로. 28px 은 새로 끼운 compact 단이다.
import { Button } from '../../components/shared';

const ACCEPT = 'image/png,image/jpeg,image/webp';
const ACCEPT_RE = /^image\/(png|jpe?g|webp)$/i;
const MAX_SOURCE_BYTES = 10 * 1024 * 1024;

/** 서버 응답(base64 | 외부 URL) → <img src>. */
function logoSrc(logo: InstitutionLogo | null): string | null {
  if (!logo) return null;
  if (logo.url) return logo.url;
  if (logo.data) return `data:${logo.contentType || 'image/png'};base64,${logo.data}`;
  return null;
}

interface Props {
  /** 로고 등록/삭제 후 갱신된 institution — 상위 폼이 logoUrl 을 동기화한다. */
  onChanged?: (inst: Institution) => void;
  /**
   * 어떤 모양으로 그릴지 (2026-08-26 추가 — 시안 적용).
   *   'group' (기본) — 콘솔에서 옮겨 온 원래 모양. 세로 그룹 + 긴 안내문.
   *   'row'          — 시안의 로고 카드 한 줄(미리보기 76px · 설명 · 단추).
   * **기본값이 곧 기존 동작**이라 이 prop 을 안 주면 예전과 한 픽셀도 다르지 않다.
   * 로직(업로드·삭제·상태)은 한 벌뿐이고 갈라지는 것은 마크업뿐이다.
   */
  variant?: 'group' | 'row';
  /**
   * 무엇을 등록하는가 (2026-08-28 · 자리 재정의 판 8).
   *   'logo' (기본) — 화면의 브랜드. **기본값이 곧 기존 동작**이라 안 주면 한 픽셀도 안 다르다.
   *   'seal'        — **문서에 찍히는 것**. 증명서의 「직인」 자리에 들어간다.
   *
   * ★ 올리는 길·검사·상태는 **한 벌뿐**이다 — 갈라지는 것은 부르는 API 와 문구뿐이다.
   *   직인을 위해 같은 화면을 한 벌 더 만들면 한쪽만 고쳐지는 날이 온다.
   */
  kind?: 'logo' | 'seal';
}

export function CompanyLogoGroup({ onChanged, variant = 'group', kind = 'logo' }: Props) {
  const seal = kind === 'seal';
  const api = seal
    ? { get: getInstitutionSeal, put: uploadInstitutionSeal, del: deleteInstitutionSeal }
    : { get: getInstitutionLogo, put: uploadInstitutionLogo, del: deleteInstitutionLogo };
  const word = seal ? '직인' : '로고';
  const inputRef = useRef<HTMLInputElement>(null);
  const [logo, setLogo] = useState<InstitutionLogo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setLogo(await api.get());
    } catch {
      setLogo(null); // 조회 실패는 "미등록"과 같게 처리 — 등록 자체는 계속 가능.
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null); setNotice(null);
    if (!ACCEPT_RE.test(file.type)) { setError('PNG·JPEG·WEBP 이미지만 등록할 수 있습니다.'); return; }
    if (file.size > MAX_SOURCE_BYTES) { setError('원본 파일은 10MB 이하만 선택할 수 있습니다.'); return; }
    setBusy(true);
    try {
      const resized = await resizeImageForUpload(file, 512);
      const inst = await api.put(resized.contentType, resized.base64);
      setLogo({ contentType: resized.contentType, data: resized.base64 });
      setNotice(seal ? '직인이 등록되었습니다. 증명서에 찍힙니다.' : '로고가 등록되었습니다. 사용자 사이트에 반영됩니다.');
      onChanged?.(inst);
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message ?? err?.response?.data?.error;
      setError(apiMsg ?? (err instanceof Error ? err.message : `${word} 등록에 실패했습니다.`));
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    setError(null); setNotice(null);
    setBusy(true);
    try {
      const inst = await api.del();
      setLogo(null);
      setNotice(seal ? '직인이 삭제되었습니다. 증명서에는 「직인」 글자만 찍힙니다.' : '로고가 삭제되었습니다. 사용자 사이트는 기본 심볼로 표시됩니다.');
      onChanged?.(inst);
    } catch (err: any) {
      const apiMsg = err?.response?.data?.message ?? err?.response?.data?.error;
      setError(apiMsg ?? `${word} 삭제에 실패했습니다.`);
    } finally {
      setBusy(false);
    }
  }

  const src = logoSrc(logo);

  /* ── 시안 모양 — 카드 안 한 줄(미리보기 · 설명 · 단추) ─────────────────────
   * 안내문은 시안 문구("PNG · SVG · 최대 2MB")를 그대로 쓰지 않았다 — 실제로 받는 것은
   * PNG·JPEG·WEBP 이고 원본 상한은 10MB 다(올릴 때 1MB 이하로 줄인다). 화면이 규칙을
   * 잘못 말하면 사용자는 되는 걸 안 된다고 믿는다. */
  if (variant === 'row') {
    return (
      <>
        <div className="ci-logo">
          <div
            className={`ci-logo__box${busy ? ' is-busy' : ''}${!loading && !src && !seal ? ' ci-logo__box--brand' : ''}`}
          >
            {loading ? (
              <span className="ci-logo__ph">불러오는 중…</span>
            ) : src ? (
              <img className="ci-logo__img" src={src} alt={`등록된 회사 ${word}`} />
            ) : seal ? (
              <span className="ci-logo__ph"><ImageIcon size={18} aria-hidden />미등록</span>
            ) : (
              /* 로고 미등록 — 화면이 실제로 쓰는 기본값(AXI 심볼)을 그대로 보여 준다.
               * 「미등록」 글자만 두면 사용자는 그 자리가 «빈 채로» 나가는 줄 안다. */
              <span className="ci-logo__ph ci-logo__ph--brand">
                <BrandSymbol width={40} height={40} />
                <span className="ci-logo__phcap">기본 심볼</span>
              </span>
            )}
          </div>

          <div className="ci-logo__meta">
            <span className="ci-logo__t">회사 {word}</span>
            <span className="ci-logo__d">PNG · JPEG · WEBP · 원본 10MB 이하 · 정사각(1:1) 권장</span>
            <span className="ci-logo__d">
              {seal
                ? '증명서의 「직인」 자리에 찍힙니다 — 등록 안 하면 지금처럼 글자만 남습니다'
                : src
                  ? '바꾸면 좌측 레일 브랜드에 바로 반영됩니다'
                  : '등록 전에는 AXI 기본 심볼이 쓰입니다 — 올리면 좌측 레일 브랜드가 바로 바뀝니다'}
            </span>
          </div>

          <div className="ci-logo__acts">
            <Button
              variant="secondary"
              size="compact"
              disabled={busy}
              loading={busy}
              iconLeft={busy ? undefined : <Upload size={13} aria-hidden />}
              onClick={() => inputRef.current?.click()}
            >
              {busy ? '처리 중…' : src ? '이미지 바꾸기' : '이미지 올리기'}
            </Button>
            {src && (
              <Button
                variant="secondary"
                size="compact"
                disabled={busy}
                iconLeft={<Trash2 size={13} aria-hidden />}
                onClick={() => void onRemove()}
              >
                지우기
              </Button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              onChange={onPick}
              hidden
              aria-hidden="true"
              tabIndex={-1}
            />
          </div>
        </div>

        {notice && <p className="ci-logo__msg ci-logo__msg--ok"><Check size={13} aria-hidden /> {notice}</p>}
        {error && <p className="ci-logo__msg ci-logo__msg--err" role="alert">{error}</p>}
      </>
    );
  }

  return (
    <div className="company-group">
      <div className="company-group__title"><ImageIcon size={14} /> 로고</div>

      <div className="company-row">
        <label className="company-row__label"><ImageIcon size={14} /> 회사 로고</label>

        <div className="company-logo">
          <div className={`company-logo__preview${busy ? ' is-busy' : ''}`}>
            {loading ? (
              <span className="company-logo__empty">불러오는 중…</span>
            ) : src ? (
              <img className="company-logo__img" src={src} alt="등록된 회사 로고" />
            ) : (
              <span className="company-logo__empty"><BrandSymbol width={40} height={40} /> 기본 심볼</span>
            )}
          </div>

          <div className="company-logo__actions">
            <button
              type="button"
              className="svc-btn svc-btn--primary"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              <Upload size={13} /> {busy ? '처리 중…' : src ? '로고 변경' : '로고 등록'}
            </button>
            {src && (
              <button type="button" className="svc-btn svc-btn--ghost" disabled={busy} onClick={() => void onRemove()}>
                <Trash2 size={13} /> 삭제
              </button>
            )}
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              onChange={onPick}
              hidden
              aria-hidden="true"
              tabIndex={-1}
            />
          </div>
        </div>

        <p className="company-row__hint">
          <strong>정사각형(1:1)로 저장됩니다</strong> — 가로/세로가 다른 이미지는 잘라내지 않고 비율을 유지한 채
          투명 여백을 넣어 정사각으로 맞춥니다. 원래 정사각인 이미지를 올리면 여백 없이 꽉 찹니다.
        </p>
        <p className="company-row__hint">
          PNG·JPEG·WEBP · 원본 10MB 이하(업로드 시 1MB 이하로 자동 축소) · 투명 배경 PNG 권장.
          등록하면 사용자 사이트 좌측 메뉴 상단이 <strong>72px 정사각</strong> 자리에 이 로고로 바뀌고,
          그 아래 회사명 텍스트는 표시되지 않습니다.
        </p>
        {notice && <p className="company-logo__ok"><Check size={13} /> {notice}</p>}
        {error && <p className="company-row__err" role="alert">{error}</p>}
      </div>
    </div>
  );
}
